"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import type {
  CBrowserPressOnlineContent,
  CBrowserPressOnlineIssue,
  CBrowserPressOnlineItem,
  CBrowserPressOnlineTag,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { CatalogLoadMore } from "@/components/cms/Pagination/CatalogLoadMore";
import {
  cBrowserPressOnlineTranslations,
  type CBrowserPressOnlineTranslations,
} from "./CBrowserPressOnline.i18n";
import type {
  FlipBookApi,
  CBrowserPressOnlineFlipBookProps,
} from "./CBrowserPressOnlineFlipBook";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CBrowserPressOnline.module.css";

/* Dynamic load CALEGO wrappera FlipBook (nie samej biblioteki react-pageflip)
   przez `./CBrowserPressOnlineFlipBook.tsx` - rozwiazuje 2 problemy:
     1. SSR-incompatibility react-pageflip (uzywa DOM directly)
     2. Ref gubi sie przez `next/dynamic` ssr:false na class component -
        wrapper opakowuje go w forwardRef + useImperativeHandle (stabilne API).
   Wymagana paczka: `npm install react-pageflip`. */
const CBrowserPressOnlineFlipBook = dynamic(
  () =>
    import("./CBrowserPressOnlineFlipBook").then((m) => m.default),
  {
    ssr: false,
    loading: () => <div className={styles.empty}>...</div>,
  },
) as React.ComponentType<
  CBrowserPressOnlineFlipBookProps & { ref?: React.Ref<FlipBookApi> }
>;

/**
 * CBrowserPressOnline - visitor view komponentu `c_browser_press_online`
 * (Przegladarka prasy online - gazetka, katalog, oferta).
 *
 * **Data source:** `C_BROWSER_PRESS_ONLINE_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   list:  { id, symbol, view:'list', items:[{ id, name, text, cover, ... }], pagination }
 *   issue: { id, symbol, view:'issue', issue:{ id, name, images:[...], file, ... }, list_href }
 *   ```
 *
 * **Dwa tryby (discriminator `content.view`):**
 *   - `list` - paginowana siatka wydan; kazdy kafel linkuje do `item.href`.
 *   - `issue` - pojedyncze wydanie: flip-book (`react-pageflip`) z paginacja
 *     dual-page na desktop / single-page swipe na mobile, lightbox dla zoomu
 *     pojedynczej strony, overlay tagow opisowych, PDF download, linki do
 *     oferty + newslettera.
 *
 * **RWD:** `react-pageflip` ma wbudowany `usePortrait: true` + breakpointy -
 * sam przelacza dual-page na single-page swipe na waskich ekranach.
 */
export function CBrowserPressOnline({ content }: CBrowserPressOnlineProps) {
  const t = useT(cBrowserPressOnlineTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {content.view === "issue" && content.issue ? (
        <IssueView
          issue={content.issue}
          listHref={content.list_href ?? null}
          t={t}
        />
      ) : (
        <ListView content={content} t={t} />
      )}
    </div>
  );
}

export type CBrowserPressOnlineProps = {
  content: CBrowserPressOnlineContent;
};

/* === List view (siatka wydan) ============================================ */

function ListView({
  content,
  t,
}: {
  content: CBrowserPressOnlineContent;
  t: CBrowserPressOnlineTranslations;
}) {
  const items: CBrowserPressOnlineItem[] = Array.isArray(content.items)
    ? content.items
    : [];
  const pagination = content.pagination;

  if (items.length === 0) {
    return <div className={styles.empty}>{t.emptyList}</div>;
  }

  return (
    <CatalogLoadMore<CBrowserPressOnlineItem>
      initialItems={items}
      initialPage={pagination?.page ?? 1}
      totalPages={pagination?.total_pages ?? 1}
      totalCount={pagination?.total_count}
      getKey={(item) => item.id}
      extractItems={(c) => {
        const arr = (c as CBrowserPressOnlineContent | null)?.items;
        return Array.isArray(arr) ? arr : [];
      }}
    >
      {(allItems) => (
        <ul className={styles.issues}>
          {allItems.map((item) => (
            <IssueCard key={item.id} item={item} t={t} />
          ))}
        </ul>
      )}
    </CatalogLoadMore>
  );
}

function IssueCard({
  item,
  t,
}: {
  item: CBrowserPressOnlineItem;
  t: CBrowserPressOnlineTranslations;
}) {
  const href = extractInternalHref(item.href);
  const appliesLine = formatAppliesLine(
    item.applies_date_start,
    item.applies_date_end,
    t,
  );

  const inner = (
    <>
      <div className={styles.coverWrap}>
        {item.cover ? (
          /* Native <img> - cross-origin URL z Imperial backendu. */
          <img
            src={item.cover.src}
            alt={item.name}
            className={styles.cover}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className={styles.coverPlaceholder} aria-hidden="true" />
        )}
        {item.count_pictures > 0 && (
          <span className={styles.countBadge}>
            {t.pagesCount(item.count_pictures)}
          </span>
        )}
      </div>
      <div className={styles.issueCardBody}>
        <h2 className={styles.issueName}>{item.name}</h2>
        {appliesLine && <p className={styles.issueDates}>{appliesLine}</p>}
      </div>
    </>
  );

  return (
    <li className={styles.issueCard}>
      {href !== null ? (
        <Link href={href} className={styles.issueCardLink} aria-label={item.name}>
          {inner}
        </Link>
      ) : (
        <a href={item.href} className={styles.issueCardLink} aria-label={item.name}>
          {inner}
        </a>
      )}
    </li>
  );
}

/* === Issue view (flip-book + lightbox + tagi) ============================ */

function IssueView({
  issue,
  listHref,
  t,
}: {
  issue: CBrowserPressOnlineIssue;
  listHref: string | null;
  t: CBrowserPressOnlineTranslations;
}) {
  const backHref = listHref ? extractInternalHref(listHref) : null;
  const appliesLine = formatAppliesLine(
    issue.applies_date_start,
    issue.applies_date_end,
    t,
  );

  /* Lightbox state - openAt = indeks strony (0-based) lub null. */
  const [lightboxAt, setLightboxAt] = useState<number | null>(null);

  /* Flip-book state - current page (0-based) + total. Aktualizowany przez
     `onFlip` z react-pageflip; pasek nawigacji pod book'em uzywa do disabled. */
  const [currentPage, setCurrentPage] = useState(0);
  const bookRef = useRef<FlipBookApi | null>(null);

  const images = issue.images;
  const hasImages = images.length > 0;

  /* Slajdy lightboxa z pelnego zbioru stron - indeks pasuje do indeksu strony. */
  const lightboxSlides = useMemo(
    () =>
      images.map((img) => ({
        src: img.full_src,
        width: img.full_width ?? img.width ?? undefined,
        height: img.full_height ?? img.height ?? undefined,
        alt: img.description || issue.name,
        description: img.description || undefined,
      })),
    [images, issue.name],
  );

  const flipPrev = useCallback(() => bookRef.current?.flipPrev(), []);
  const flipNext = useCallback(() => bookRef.current?.flipNext(), []);
  const onFlip = useCallback((pageIndex: number) => {
    setCurrentPage(pageIndex);
  }, []);

  /* Klawiatura - lewo/prawo nawigacja po stronach (gdy nie w lightboxie). */
  useEffect(() => {
    if (lightboxAt !== null) return;
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return;
      if (e.key === "ArrowLeft") flipPrev();
      else if (e.key === "ArrowRight") flipNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flipPrev, flipNext, lightboxAt]);

  /* Wymiary strony - z konfiguracji wydania (admin ustawia per gazetka).
     Fallback'i na rozsadne defaulty zeby flip-book renderowal nawet gdy
     admin pominal pole. */
  const pageWidth = issue.page_size_width > 0 ? issue.page_size_width : 490;
  const pageHeight = issue.page_size_height > 0 ? issue.page_size_height : 720;

  return (
    <article>
      {(backHref !== null || listHref) && (
        <div className={styles.issueBack}>
          {backHref !== null ? (
            <Link href={backHref} className={styles.issueBackLink}>
              {t.backToList}
            </Link>
          ) : (
            <a href={listHref!} className={styles.issueBackLink}>
              {t.backToList}
            </a>
          )}
        </div>
      )}

      <header className={styles.issueHeader}>
        <h2 className={styles.issueTitle}>{issue.name}</h2>
        {appliesLine && <p className={styles.issueAppliesLine}>{appliesLine}</p>}
        {issue.text && (
          /* Backend zwraca text z editora WYSIWYG - trusted source z admin
             panelu, OK dla `dangerouslySetInnerHTML`. */
          <div
            className={styles.issueLead}
            dangerouslySetInnerHTML={{ __html: issue.text }}
          />
        )}
      </header>

      {(issue.file || issue.more_url || issue.newsletter_url) && (
        <div className={styles.actions}>
          {issue.file && (
            <a
              className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
              href={issue.file.src}
              download={issue.file.name}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.download}
            </a>
          )}
          {issue.more_url && (
            <a
              className={styles.actionBtn}
              href={issue.more_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.offer}
            </a>
          )}
          {issue.newsletter_url && (
            <a
              className={styles.actionBtn}
              href={issue.newsletter_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.newsletter}
            </a>
          )}
        </div>
      )}

      {!hasImages ? (
        <div className={styles.empty}>{t.emptyIssue}</div>
      ) : (
        <>
          <div className={styles.bookWrap}>
            <CBrowserPressOnlineFlipBook
              ref={bookRef}
              width={pageWidth}
              height={pageHeight}
              onFlip={onFlip}
            >
              {/* WAZNE: react-pageflip wymaga aby children byly natywnymi divami
                  (NIE function componentami) bo iteruje przez DOM children w
                  componentDidMount i sprawdza geometrie. Inline rendering. */}
              {images.map((img, i) => (
                <div key={img.id} className={styles.page}>
                  <img
                    src={img.thumb_src}
                    alt={img.description || t.pageLabel(i + 1)}
                    className={styles.pageImg}
                    loading={i < 4 ? "eager" : "lazy"}
                    decoding="async"
                    draggable={false}
                  />

                  {/* Overlay tagow - koordynaty w pikselach miniatury, przeliczane na %. */}
                  {img.tags.length > 0 && img.width && img.height && (
                    <div className={styles.tagsLayer}>
                      {img.tags.map((tag) => (
                        <TagOverlay
                          key={tag.id}
                          tag={tag}
                          imgWidth={img.width!}
                          imgHeight={img.height!}
                        />
                      ))}
                    </div>
                  )}

                  <button
                    type="button"
                    className={styles.pageZoomBtn}
                    onClick={(e) => {
                      /* react-pageflip propaguje click do flip handler'a -
                         stopPropagation zapobiega odpaleniu flip'a przy kliku
                         w lupke. */
                      e.stopPropagation();
                      setLightboxAt(i);
                    }}
                    aria-label={t.openPage}
                    title={t.openPage}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="11" cy="11" r="8" />
                      <path d="m21 21-4.3-4.3" />
                      <line x1="11" y1="8" x2="11" y2="14" />
                      <line x1="8" y1="11" x2="14" y2="11" />
                    </svg>
                  </button>

                  {!issue.hide_page_numbering && (
                    <span className={styles.pageNumber} aria-hidden="true">
                      {t.pageLabel(i + 1)}
                    </span>
                  )}
                </div>
              ))}
            </CBrowserPressOnlineFlipBook>

            <div className={styles.bookNav}>
              <button
                type="button"
                className={styles.bookNavBtn}
                onClick={flipPrev}
                disabled={currentPage <= 0}
                aria-label={t.pageLabel(Math.max(1, currentPage))}
              >
                {"<-"}
              </button>
              <span className={styles.bookNavInfo}>
                {t.pageLabel(currentPage + 1)} / {issue.total_pages}
              </span>
              <button
                type="button"
                className={styles.bookNavBtn}
                onClick={flipNext}
                disabled={currentPage >= issue.total_pages - 1}
                aria-label={t.pageLabel(
                  Math.min(issue.total_pages, currentPage + 2),
                )}
              >
                {"->"}
              </button>
            </div>
          </div>

          <Lightbox
            open={lightboxAt !== null}
            index={lightboxAt ?? 0}
            close={() => setLightboxAt(null)}
            slides={lightboxSlides}
            plugins={[Zoom]}
            zoom={{
              maxZoomPixelRatio: 3,
              scrollToZoom: true,
            }}
          />
        </>
      )}
    </article>
  );
}

function TagOverlay({
  tag,
  imgWidth,
  imgHeight,
}: {
  tag: CBrowserPressOnlineTag;
  imgWidth: number;
  imgHeight: number;
}) {
  /* Koordynaty z backendu sa w pikselach miniatury 'm' - przeliczamy na %
     zeby skalowalo sie z `object-fit: contain` na pageImg. */
  const style: React.CSSProperties = {
    top: `${(tag.y / imgHeight) * 100}%`,
    left: `${(tag.x / imgWidth) * 100}%`,
    width: `${(tag.width / imgWidth) * 100}%`,
    height: `${(tag.height / imgHeight) * 100}%`,
  };
  return (
    <div
      className={styles.tag}
      style={style}
      onClick={(e) => e.stopPropagation()}
      tabIndex={0}
      role="button"
      aria-label={tag.name}
    >
      {(tag.name || tag.description) && (
        <div className={styles.tagPopup}>
          {tag.name && <div className={styles.tagPopupTitle}>{tag.name}</div>}
          {tag.description && <div>{tag.description}</div>}
        </div>
      )}
    </div>
  );
}

/* === Helpers ============================================================= */

function formatAppliesLine(
  start: string | null,
  end: string | null,
  t: CBrowserPressOnlineTranslations,
): string | null {
  if (!start && !end) return null;
  if (start && end) {
    return `${t.applies} ${t.from} ${t.formatDate(start)} ${t.to} ${t.formatDate(end)}`;
  }
  if (start) {
    return `${t.applies} ${t.from} ${t.formatDate(start)}`;
  }
  return `${t.applies} ${t.to} ${t.formatDate(end!)}`;
}

